import { Controller } from "@hotwired/stimulus"

const ICONS = {
  positive: "bi-emoji-smile",
  neutral: "bi-emoji-neutral",
  negative: "bi-emoji-frown",
}
const COLORS = {
  positive: "text-sentiment-positive-deep",
  neutral: "text-sentiment-neutral-deep",
  negative: "text-sentiment-negative-deep",
}

export default class extends Controller {
  static targets = ["trigger", "popover", "option", "statusLine", "reason", "save", "badgeIcon", "badgeLabel"]
  static values = {
    mentionId: String,
    currentLabel: String,
    autoLabel: String,
    autoConfidence: String,
    isOverridden: Boolean,
    overriddenBy: String,
    overriddenAt: String,
  }

  connect() {
    this.#pendingLabel = null
    this.#outsideHandler = this.#closeOnOutside.bind(this)
  }

  disconnect() {
    window.removeEventListener("click", this.#outsideHandler)
  }

  toggle(event) {
    event.preventDefault()
    event.stopPropagation()

    if (this.popoverTarget.classList.contains("hidden")) {
      this.#open()
    } else {
      this.#close()
    }
  }

  pick(event) {
    this.#pendingLabel = event.params.label
    this.#highlightOption(this.#pendingLabel)
  }

  // On an unclassified mention there's nothing to fall back to, and submitting a
  // blank label would clear rather than set — so Save is a no-op until one is picked.
  save() {
    const label = this.#pendingLabel || this.currentLabelValue
    if (!label) return
    this.#submit(label, this.reasonTarget.value.trim())
  }

  // Set the sentiment directly, no popover — used by the Review-mode shortcuts.
  setLabel(label) {
    this.#submit(label, "")
  }

  async #submit(label, reason) {
    const response = await fetch(`/mentions/${this.mentionIdValue}/sentiment`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector("meta[name=csrf-token]")?.content,
        Accept: "text/vnd.turbo-stream.html",
      },
      body: JSON.stringify({ label, reason }),
    })

    if (response.ok) {
      const html = await response.text()
      Turbo.renderStreamMessage(html)
      this.#close()
    } else {
      window.toast("Could not save sentiment. Please try again.", { kind: "error" })
    }
  }

  cancel() {
    this.#close()
  }

  // private

  #pendingLabel = null
  #outsideHandler = null

  #open() {
    this.#pendingLabel = null
    this.reasonTarget.value = ""
    this.#renderStatusLine()
    this.#highlightOption(this.currentLabelValue)
    this.popoverTarget.classList.remove("hidden")
    this.#card()?.classList.remove("overflow-hidden")
    window.addEventListener("click", this.#outsideHandler)
  }

  #close() {
    this.popoverTarget.classList.add("hidden")
    this.#card()?.classList.add("overflow-hidden")
    window.removeEventListener("click", this.#outsideHandler)
  }

  #closeOnOutside(event) {
    if (!this.element.contains(event.target)) this.#close()
  }

  #card() {
    return this.element.closest(".thread-card")
  }

  #renderStatusLine() {
    const el = this.statusLineTarget
    if (this.isOverriddenValue) {
      const by = this.overriddenByValue || "a team member"
      const at = this.overriddenAtValue
        ? this.#relativeTime(new Date(this.overriddenAtValue))
        : ""
      const autoDesc = this.autoLabelValue
        ? ` The model suggested ${this.autoLabelValue} (${this.autoConfidenceValue} confidence).`
        : ""
      el.textContent = `Set by ${by}${at ? ` · ${at}` : ""} — not auto-classified.${autoDesc}`
    } else if (this.autoLabelValue) {
      const conf = this.autoConfidenceValue ? ` · ${this.autoConfidenceValue} confidence` : ""
      el.textContent = `Auto-classified · ${this.autoLabelValue}${conf}`
    } else {
      el.textContent = "Not classified yet — pick one below."
    }
  }

  #highlightOption(label) {
    this.optionTargets.forEach((btn) => {
      const isActive = btn.dataset.sentimentOverrideLabelParam === label
      const activeClass = btn.dataset.activeClass.split(" ")
      if (isActive) {
        btn.classList.add("border-2", ...activeClass)
        btn.classList.remove("border-transparent")
      } else {
        btn.classList.remove("border-2", ...activeClass)
        btn.classList.add("border-transparent")
      }
    })
  }

  #relativeTime(date) {
    const diff = Math.round((Date.now() - date) / 1000)
    if (diff < 60) return "just now"
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`
    return `${Math.floor(diff / 86400)}d ago`
  }
}
